import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {handleErrorResponse} from "../utils/util";
import { USER_ROLE } from "../constants/index";
import { ContentService, MasterService } from "../api/services.gen";
import { ContentDetailRetrieveData, ContentContentRankingListData, MasterOptionsMasterListData, ContentUploadFileCreateData } from "../api/types.gen";
import i18n from 'i18next';
import { resetAppState } from "../store/common";

export const fetchRankingList = createAsyncThunk(
  "fetchRankingList",
  async (data: ContentContentRankingListData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentContentRankingList(data);
      if (res.results.success) {
        return res;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const getNews = createAsyncThunk(
  "getNews",
  async (data: ContentDetailRetrieveData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentDetailRetrieve(data);
      if (res.id) {
        return res;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const uploadContentFiles = createAsyncThunk(
  "uploadContentFiles",
  async (data: ContentUploadFileCreateData, thunkAPI: any) => {
    try {
      const res: any = await ContentService.contentUploadFileCreate(data);
      if (res.success) {
        return res;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

export const getOptionsMaster = createAsyncThunk(
  "getOptionsMaster",
  async (data: MasterOptionsMasterListData, thunkAPI: any) => {
    try {
      const res: any = await MasterService.masterOptionsMasterList(data);
      if (res.success) {
        return res.data;
      } else {
        if(res.message) return thunkAPI.rejectWithValue(res.message);
        else return thunkAPI.rejectWithValue(i18n.t('MSG_GLOBAL_DATA_LOAD_FAILED'));
      }
    } catch (error: any) {
      return handleErrorResponse(error, USER_ROLE.COMPANY, thunkAPI);
    }
  },
);

// Initial state of Redux
interface ContentState {
  rankingList: [];
  loading: boolean;
  error: string | null;
  success: string | null;
  countItems: number;
  countPage: number;
  currentPage: number;
  currentPageContentRanking: number;
  pageSize: number;
  items: [];
  actionStatus: string;
  actionName: string;
  optionsMaster: [];
  rankAOnly: number;
  selectedTypeOfTalent: any;
  isUploadImageError: boolean;
}

const initialState: ContentState = {
  rankingList: [],
  loading: false,
  error: null,
  success: null,
  countItems: 0,
  countPage: 1,
  currentPage: 1,
  currentPageContentRanking: 1,
  pageSize: 10,
  items: [],
  actionStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  actionName: "",
  optionsMaster: [],
  rankAOnly: 0,
  selectedTypeOfTalent: [],
  isUploadImageError: false,
  errorMainInageState: '',
};

export const contentRankingSlice = createSlice({
  name: 'contentRanking',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setCurrentPageContentRanking: (state, action) => {
      state.currentPageContentRanking = action.payload;
    },
    setRankAOnly: (state, action) => {
      state.rankAOnly = action.payload;
    },
    setSelectedTypeOfTalent: (state, action) => {
      state.selectedTypeOfTalent = action.payload;
    },
    setIsUploadImageError: (state, action) => {
      state.isUploadImageError = action.payload;
    },
    setErrorMainInageState: (state, action) => {
      state.errorMainInageState = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // reset state
      .addCase(resetAppState, () => initialState)

      // fetchRankingList
      .addCase(fetchRankingList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRankingList.fulfilled, (state:any, action) => {
        state.loading = false;
        state.rankingList = action.payload.results.data;
        state.countItems = action.payload.count;
        state.countPage = Math.ceil(state.countItems / state.pageSize);
      })
      .addCase(fetchRankingList.rejected, (state:any, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // getNews
      .addCase(getNews.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.actionStatus = "pending";
        state.actionName = "getNews";
      })
      .addCase(getNews.fulfilled, (state:any, action) => {
        state.loading = false;
        state.items = action.payload;
        state.actionStatus = "fulfilled";
      })
      .addCase(getNews.rejected, (state:any, action) => {
        state.loading = false;
        state.error = action.payload;
        state.actionStatus = "rejected";
      })
      
      // getOptionsMaster
      .addCase(getOptionsMaster.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.actionStatus = "pending";
        state.actionName = "getNews";
      })
      .addCase(getOptionsMaster.fulfilled, (state:any, action) => {
        state.loading = false;
        state.optionsMaster = action.payload;
        state.actionStatus = "fulfilled";
      })
      .addCase(getOptionsMaster.rejected, (state:any, action) => {
        state.loading = false;
        state.error = action.payload;
        state.actionStatus = "rejected";
      });
  },
});
export const {
  setCurrentPage, setRankAOnly, setSelectedTypeOfTalent, setIsUploadImageError, setErrorMainInageState,
  setCurrentPageContentRanking,
} = contentRankingSlice.actions;
export default contentRankingSlice.reducer;